/**
 * SimpleVolumeCalc — (dose / per_volume) * tankVolumeGal = total dose.
 *
 * Renders one row per dosing option. Supports result_conversions.
 */
import { useState } from 'react';
import type { Calculator } from '@reefapp/shared';
import type { SimpleVolumeOption } from '@reefapp/shared';

type SimpleVolumeCalcShape = Calculator & { kind: 'simple_volume' };

export interface SimpleVolumeCalcProps {
  calculator: SimpleVolumeCalcShape;
  tankVolumeGal: number;
}

function formatNum(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2).replace(/\.?0+$/, '') : '—';
}

function renderConversions(
  primaryValue: number,
  _primaryUnit: string,
  conversions?: Record<string, number>,
) {
  if (!conversions || Object.keys(conversions).length === 0) return null;
  const parts: string[] = [];
  for (const [unit, factor] of Object.entries(conversions)) {
    const converted = primaryValue / factor;
    parts.push(`${formatNum(converted)} ${unit}`);
  }
  if (parts.length === 0) return null;
  return (
    <p className="text-xs text-slate-500 mt-1">
      ≈ {parts.join(' = ')}
    </p>
  );
}

export function SimpleVolumeCalc({
  calculator,
  tankVolumeGal,
}: SimpleVolumeCalcProps) {
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);

  const option = calculator.options[selectedOptionIdx];
  if (!option) return null;

  const dose = tankVolumeGal > 0
    ? (option.dose / option.per_volume) * tankVolumeGal
    : 0;
  const hasVolume = tankVolumeGal > 0;

  return (
    <div>
      <h3 className="text-lg font-semibold text-cyan-300 mb-4">
        Dosage Calculator
      </h3>

      {/* Option selector */}
      {calculator.options.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-400 mb-2">
            Dosage Scenario:
          </label>
          <select
            value={selectedOptionIdx}
            onChange={(e) => setSelectedOptionIdx(Number(e.target.value))}
            className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-sm text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            {calculator.options.map((o: SimpleVolumeOption, i: number) => (
              <option key={i} value={i}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Dose formula display */}
      <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
        <p className="text-sm text-slate-400">
          {option.name}:{' '}
          <span className="text-slate-300">
            {option.dose} {option.dose_unit} / {option.per_volume} gal
          </span>
        </p>

        {hasVolume ? (
          <div className="mt-3">
            <p className="text-2xl font-bold text-cyan-300">
              {formatNum(dose)} {option.dose_unit}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              For {formatNum(tankVolumeGal)} gallons
            </p>
            {renderConversions(dose, option.dose_unit, option.result_conversions)}
          </div>
        ) : (
          <div className="mt-3">
            <p className="text-sm text-amber-400">
              Enter your tank volume (left sidebar) to calculate the dose.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Dose = ({option.dose} {option.dose_unit} × {formatNum(tankVolumeGal)} gal) / {option.per_volume} gal
            </p>
          </div>
        )}
      </div>

      {/* Notes */}
      {calculator.notes && (
        <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
          <p className="text-xs text-slate-400 whitespace-pre-wrap">
            {calculator.notes}
          </p>
        </div>
      )}
    </div>
  );
}